import type { DriveStep, DriverHook } from 'driver.js';

import { useCalculatorStore } from '../../store/calculatorStore';

/** Must match `useTutorial` logic that forces visibility during the rename/close step. */
export const TOUR_SCENARIO_TAB_ACTIONS_SELECTOR = '[data-tour="tour-scenario-tab-actions"]';

/** Amortization card on the calculator (`MortgageCalculator`); listeners expand year one for the tour Extra column. */
export const TOUR_AMORTIZATION_TABLE_SELECTOR = '[data-tour="tour-amortization-table"]';

/** Dispatched when the guided tour highlights the amortization step so the first year opens by default. */
export const TOUR_EXPAND_AMORT_FIRST_YEAR_EVENT = 'mortgagecalc:tour-expand-amort-first-year';

/** Compare tab: winner banner card (`ComparisonView`). */
export const TOUR_COMPARISON_WINNER_SELECTOR = '[data-tour="tour-comparison-winner"]';

/** Total Driver stops across both instances (11 scenario steps + full comparison + 7 Compare steps). */
export const TOUR_TOTAL_STEPS = 19;

function tourProgress(oneBasedStep: number): string {
  return `Step ${oneBasedStep} of ${TOUR_TOTAL_STEPS}`;
}

/** Must match `ComparisonView` metrics grid vs scenario cards (`tabsWithData.length === 2`). */
export function isTwoScenarioCompareTour(): boolean {
  const tabs = useCalculatorStore.getState().calculatorTabs;
  const withData = tabs.filter(
    t => Boolean(t.data?.amortizationSchedule && t.data.amortizationSchedule.length > 0),
  );
  return withData.length === 2;
}

const POP_QUICKSTART = {
  title: 'Quick-start presets',
  description:
    'Not sure where to start? Click any preset pill to load a realistic scenario, then adjust from there.',
  side: 'bottom' as const,
  align: 'start' as const,
};

const POP_SCENARIO_TABS = {
  title: 'Each tab is its own scenario',
  description:
    'Each tab has its own inputs: rate, loan program, down payment, expenses. Nothing carries between them. You can have up to three open at once.',
  side: 'bottom' as const,
  align: 'start' as const,
};

const POP_COMPARE_TAB = {
  title: 'The Compare tab',
  description:
    'Open two tabs and Compare appears automatically. It pulls in a winner banner, balance charts, a metrics grid, and a full comparison table.',
  side: 'bottom' as const,
  align: 'end' as const,
};

const POP_ACTIVE_TAB = {
  title: 'Edit any scenario',
  description:
    'Click a tab to switch to its inputs: home price, rate, loan program, amortization period. All independent.',
  side: 'bottom' as const,
  align: 'start' as const,
};

const POP_TAB_RENAME_CLOSE = {
  title: 'Rename or remove a tab',
  description:
    'Hover over a tab to see the rename and close icons. Names go up to 24 characters. Closing asks you to confirm first since the inputs can\'t be recovered.',
  side: 'bottom' as const,
  align: 'start' as const,
};

const POP_EXPORT_ACTIONS = {
  title: 'Save, Share, Download',
  description:
    'Save compresses your scenario into the page URL; bookmark it to return later. Share gives you a link to send. Download exports a formatted Excel file with inputs, key numbers, and amortization schedules for every open tab.',
  side: 'bottom' as const,
  align: 'end' as const,
};

const POP_MORTGAGE_TERMS = {
  title: 'Mortgage Terms',
  description:
    'Home price, down payment, and rate are always visible at the top. Click Advanced for the rest: mortgage type, loan program, amortization period, payment frequency. Accelerated bi-weekly is in there and can cut years off your payoff.',
  side: 'right' as const,
  align: 'start' as const,
};

const POP_MONTHLY = {
  title: 'Monthly Homeowner Expenses',
  description:
    'Property taxes, insurance, and HOA fees stack on top of your P&I payment. Enter each as a fixed dollar amount or a percentage of the home price. They show up in your total monthly figure and in the amortization schedule.',
  /* At ~1024px the left column is wide enough that `right` runs out of viewport room; Driver then falls back to `top` and the tip floats away. Bottom stays tied to this card. */
  side: 'bottom' as const,
  align: 'start' as const,
};

const POP_EXTRA = {
  title: 'Extra Payments',
  description:
    'Set a recurring extra payment applied to principal on any frequency. Add a start date and an optional end date if the payments are temporary. Total interest and payoff date update when you save.',
  side: 'right' as const,
  align: 'start' as const,
};

const POP_AMORT = {
  title: 'Amortization Schedule',
  description:
    'Payments are grouped by year. Expand any year to see the month-by-month breakdown: principal, interest, expenses, and remaining balance.<br /><br />Click the Extra Payments cell on any row to enter a one-time lump sum extra payment.',
  side: 'top' as const,
  align: 'start' as const,
};

const POP_INSIGHTS = {
  title: 'Live payment summary',
  description:
    'On desktop, the panel on the right updates as you type. On mobile, the same live total stays in the bar at the bottom of the screen. Both show your monthly payment broken down by component - principal, interest, taxes, insurance, and PMI - plus lifetime totals and projected payoff date.',
  side: 'left' as const,
  align: 'start' as const,
};

const POP_FULL_COMPARE = {
  title: 'Open the full comparison',
  description:
    'This opens the full side-by-side analysis: winner banner, metrics grid, balance chart, and a comparison table with winning cells highlighted.',
  side: 'left' as const,
  align: 'end' as const,
};

const POP_COMPARISON_NAV = {
  title: 'Comparison overview',
  description: 'The navigation strip lets you jump to any section.',
  side: 'bottom' as const,
  align: 'start' as const,
};

const POP_COMPARISON_WINNER = {
  title: 'Winner banner',
  description:
    'The winner banner names the better scenario with the headline numbers: total interest saved, monthly payment difference, and how many months earlier it pays off.',
  side: 'bottom' as const,
  align: 'start' as const,
};

const POP_COMPARISON_INSIGHTS = {
  title: 'Insight strip',
  description:
    'The dark panel at the bottom covers break-even timing, the cost impact of the rate gap, and which scenario builds more equity by year five. Easy to scroll past, worth a look.',
  side: 'bottom' as const,
  align: 'start' as const,
};

const POP_COMPARISON_TABLE = {
  title: 'Comparison table',
  description:
    'Three collapsible sections let you compare any figure directly: Mortgage Info, Payment Breakdown, and Lifetime Totals. Winning cells are green; key rows show the exact difference as a badge.',
  side: 'bottom' as const,
  align: 'start' as const,
};

const POP_COMPARISON_METRICS_GRID = {
  title: 'Metrics grid',
  description:
    'The four panels below the banner show Monthly Payment, Total Interest, Total Cost, and Payoff Date as proportional bars. The callout under each states the difference plainly, so you don\'t have to calculate it.',
  side: 'top' as const,
  align: 'start' as const,
};

const POP_COMPARISON_METRICS_ALT = {
  title: 'Scenario snapshot cards',
  description:
    'Each open scenario gets a card with rate, home size, and monthly payment at a glance. Use them as a quick check before diving into the table and charts below.',
  side: 'top' as const,
  align: 'start' as const,
};

const POP_COMPARISON_CHARTS = {
  title: 'Balance chart',
  description:
    'The area chart shows all balances from first payment to payoff. The shaded gap between the curves marks where and how fast the two scenarios diverge.',
  side: 'top' as const,
  align: 'center' as const,
};

const POP_COMPARISON_BREAKDOWN = {
  title: 'Cost breakdown',
  description:
    'This breaks total spend into categories side by side: principal, interest, down payment, PMI, and expenses. The interest gap is often larger than the monthly payment difference suggests.',
  side: 'top' as const,
  align: 'start' as const,
};

function stepsThroughKeyInsights(): DriveStep[] {
  const segments: Pick<DriveStep, 'element' | 'popover'>[] = [
    { element: '[data-tour="tour-quickstart-pills"]', popover: POP_QUICKSTART },
    { element: '[data-tour="tour-scenario-tabs"]', popover: POP_SCENARIO_TABS },
    { element: '[data-tour="tour-compare-tab"]', popover: POP_COMPARE_TAB },
    { element: '[data-tour="tour-calculator-export-actions"]', popover: POP_EXPORT_ACTIONS },
    { element: '[data-tour="tour-calculator-tab-active"]', popover: POP_ACTIVE_TAB },
    { element: TOUR_SCENARIO_TAB_ACTIONS_SELECTOR, popover: POP_TAB_RENAME_CLOSE },
    { element: '[data-tour="tour-mortgage-terms"]', popover: POP_MORTGAGE_TERMS },
    { element: '[data-tour="tour-monthly-expenses"]', popover: POP_MONTHLY },
    { element: '[data-tour="tour-extra-payments"]', popover: POP_EXTRA },
    { element: TOUR_AMORTIZATION_TABLE_SELECTOR, popover: POP_AMORT },
    { element: '[data-tour="tour-key-insights-panel"]', popover: POP_INSIGHTS },
  ];
  return segments.map((seg, idx) => ({
    element: seg.element,
    popover: { ...seg.popover, progressText: tourProgress(idx + 1) },
  }));
}

function stepViewFullComparison(onNextClick?: DriverHook): DriveStep {
  return {
    element: '[data-tour="tour-compare-full-button"]',
    popover: {
      ...POP_FULL_COMPARE,
      /** Last step of first Driver instance; without this Driver labels the primary button `Done`. */
      progressText: tourProgress(12),
      nextBtnText: 'Next &rarr;',
      ...(onNextClick ? { onNextClick } : {}),
    },
  };
}

/** Steps 13-19 after destroying the first Driver instance (avoids a stale popover on the Compare CTA). */
export function buildCompareContinuationSteps(): DriveStep[] {
  const metricsPopover = isTwoScenarioCompareTour() ? POP_COMPARISON_METRICS_GRID : POP_COMPARISON_METRICS_ALT;

  return [
    {
      element: '[data-tour="tour-comparison-nav"]',
      popover: {
        ...POP_COMPARISON_NAV,
        progressText: tourProgress(13),
      },
    },
    {
      element: TOUR_COMPARISON_WINNER_SELECTOR,
      popover: {
        ...POP_COMPARISON_WINNER,
        progressText: tourProgress(14),
      },
    },
    {
      element: '[data-tour="tour-comparison-insights"]',
      popover: {
        ...POP_COMPARISON_INSIGHTS,
        progressText: tourProgress(15),
      },
    },
    {
      element: '[data-tour="tour-comparison-table"]',
      popover: {
        ...POP_COMPARISON_TABLE,
        progressText: tourProgress(16),
      },
    },
    {
      element: '[data-tour="tour-comparison-metrics"]',
      popover: {
        ...metricsPopover,
        progressText: tourProgress(17),
      },
    },
    {
      element: '[data-tour="tour-comparison-charts"]',
      popover: {
        ...POP_COMPARISON_CHARTS,
        progressText: tourProgress(18),
      },
    },
    {
      element: '[data-tour="tour-comparison-breakdown"]',
      popover: {
        ...POP_COMPARISON_BREAKDOWN,
        progressText: tourProgress(19),
      },
    },
  ];
}

/** First-time tour: 12 steps in the first leg; last step hands off to Compare via `onNextClick`. */
export function buildInitialTutorialSteps(step10OnNext: DriverHook): DriveStep[] {
  return [...stepsThroughKeyInsights(), stepViewFullComparison(step10OnNext)];
}
